import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import ConfigTable from "../../Common/Tables/ConfigTable";
import { Button, PageHeading, Icon, ConfirmDialog, employeeID, companyID } from "../../Common/UIComponents";
import {
  fetchMappingList,
  fetchAllDropdownData,
  deleteShiftMapping,
} from "../../API_Service/AssociateShiftMapping_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const getShiftIcon = (shiftName) => {
  const name = shiftName?.toLowerCase() || "";
  if (name.includes("morning") || name.includes("first") || name.includes("fs")) return "wb_sunny";
  if (name.includes("night") || name.includes("third")) return "dark_mode";
  return "schedule";
};

const getShiftIconColor = (shiftName) => {
  const name = shiftName?.toLowerCase() || "";
  if (name.includes("morning") || name.includes("first")) return "text-amber-500";
  if (name.includes("night") || name.includes("third")) return "text-indigo-500";
  return "text-[#005b60]";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const buildRows = (list = []) =>
  list.map((item) => ({
    id: item.AssociateShiftMappingId,
    labName: item.Facility_Name || "N/A",
    associateName: item.EmployeeName || "N/A",
    shiftType: item.Shift_Name || "N/A",
    shiftTime: `${item.Shift_Start_Time || ""} - ${item.Shift_End_Time || ""}`,
    dateRange: `${formatDate(item.FromDate)} - ${formatDate(item.ToDate)}`,
  }));

const EXPORT_COLUMNS = [
  { key: "associateName", title: "Associate Name" },
  { key: "labName",       title: "Facility Name" },
  { key: "shiftType",     title: "Shift Type" },
  { key: "shiftTime",     title: "Shift Time" },
  { key: "dateRange",     title: "Date Range" },
];

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows, totalShifts }) => {
  const total = rows.length;

  const stats = [
    { label: "Total Mappings",  value: total,   icon: "electrical_services", iconBg: "bg-[#005b60]/10", iconColor: "text-[#005b60]" },
    { label: "Shift Types",     value: totalShifts, icon: "sensors",  iconBg: "bg-amber-500/10", iconColor: "text-amber-500" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
      {stats.map((s) => (
        <div key={s.label} className="bg-white p-4 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50">
          <p className="text-[#3e494a] text-xs font-bold uppercase tracking-widest mb-2">{s.label}</p>
          <div className="flex items-center gap-3">
            <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
              <span className="material-symbols-outlined text-[20px]">{s.icon}</span>
            </div>
            <h3 className="text-2xl font-black text-[#181c1d]">{s.value}</h3>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function ShiftMappingPage() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user }   = useSelector((state) => state.auth);
  const companyId  = user?.CompanyId || localStorage.getItem("companyId");
  const { list = [], stats } = useSelector((state) => state.shiftMapping);

  const [searchQuery,  setSearchQuery]  = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [editSuccess,  setEditSuccess]  = useState(false);
  const [successMsg,   setSuccessMsg]   = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [isDeleting,   setIsDeleting]   = useState(false);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchAllDropdownData({ companyId }));
      dispatch(fetchMappingList(companyId));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMsg(location.state.successMessage);
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const rows = useMemo(() => buildRows(list), [list]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.labName?.toLowerCase().includes(q) ||
        r.associateName?.toLowerCase().includes(q) ||
        r.shiftType?.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const totalPages   = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleEdit           = useCallback((row) => navigate(`/shift-mapping/edit/${row.id}`), [navigate]);
  const handleAddNew         = useCallback(() => navigate("/shift-mapping/create"), [navigate]);
  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);
  const handleDelete         = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete   = useCallback(() => setDeleteDialog({ open: false, row: null }), []);

  const handleConfirmDelete = useCallback(async () => {
    const { row } = deleteDialog;
    if (!row) return;

    setIsDeleting(true);
    try {
      const result = await dispatch(deleteShiftMapping(row.id)).unwrap();
      console.log("Delete response:", result);

      if (result) {
        setDeleteDialog({ open: false, row: null });
        await new Promise(resolve => setTimeout(resolve, 500));
        dispatch(fetchMappingList(companyId));
        setEditSuccess(true);
        setSuccessMsg("Shift mapping deleted successfully.");
        setTimeout(() => setEditSuccess(false), 3000);
      } else {
        setEditSuccess(true);
        setSuccessMsg("Failed to delete. Please try again.");
        setTimeout(() => setEditSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Delete error:", error);
      setEditSuccess(true);
      setSuccessMsg("Failed to delete. Please try again.");
      setTimeout(() => setEditSuccess(false), 3000);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog, companyId, dispatch]);

  const columns = useMemo(() => [
    
    {
      key: "associateName",
      title: "ASSOCIATE NAME",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#005b60]/10 flex items-center justify-center text-[10px] font-bold text-[#005b60] flex-shrink-0">
            {row.associateName?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-sm text-[#181c1d] font-medium">{row.associateName}</span>
        </div>
      ),
    },
    {
      key: "labName",
      title: "FACILITY NAME",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60] flex-shrink-0">
            <span className="material-symbols-outlined text-[18px]">cell_tower</span>
          </div>
          <span className="font-bold text-[#181c1d]">{row.labName}</span>
        </div>
      ),
    },
    {
      key: "shiftType",
      title: "SHIFT TYPE",
      render: (row) => (
        <div className="inline-flex items-center gap-2 bg-[#e5e9e9] px-3 py-1.5 rounded-full">
          <span className={`material-symbols-outlined text-[16px] ${getShiftIconColor(row.shiftType)}`}>
            {getShiftIcon(row.shiftType)}
          </span>
          <span className="text-xs font-semibold text-[#181c1d]">{row.shiftType}</span>
        </div>
      ),
    },
    {
      key: "shiftTime",
      title: "SHIFT TIME",
      render: (row) => (
        <span className="text-sm text-[#3e494a]">{row.shiftTime || "—"}</span>
      ),
    },
    {
      key: "dateRange",
      title: "DATE RANGE",
      render: (row) => (
        <span className="text-sm text-[#3e494a]">{row.dateRange || "—"}</span>
      ),
    },
  ], []);

  return (
    <DashboardLayout>
      <div className="px-8 py-5 bg-[#f7fafa] text-[#181c1d] min-h-screen">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>User Management</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Shift Mapping</span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-wrap justify-between items-end mb-5 gap-4">
          <PageHeading
            title="Lab Associate Shift Mapping"
            subtitle="Manage personnel allocation shifts and lab facilities."
          />
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add New Mapping
          </Button>
        </div>

        {/* Stats */}
        <StatsRow rows={rows} totalShifts={stats?.totalShifts ?? 0} />

        {/* Table */}
        <ConfigTable
          columns={columns}
          rows={paginatedRows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="shift-mappings"
          emptyText="No shift mappings found."
        />

        <ConfirmDialog
          isOpen={deleteDialog.open}
          variant="danger"
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>

      {editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          {successMsg}
        </div>
      )}
    </DashboardLayout>
  );
}
